import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from './Icon'
import type { CoverageEntry } from '../data/catalog'

// Coverage modal for a regional eSIM — the full list of destinations the
// plan roams onto, with each country's partner carriers. Opened from the
// plan page's Coverage card. Searchable by country name; the list is
// sorted alphabetically. Closes on backdrop click, Escape, or the X.
//
// Carrier tech: most packs are uniformly 4G/5G, stated once in the meta
// line. Packs with exceptions carry a "(4G)"/"(3G)" marker inside the
// affected carrier's nets string (e.g. the Asia-Pacific pack's Vietnam
// row); when any entry has one, the meta line softens to "most networks"
// so we never overclaim.

interface Props {
  open: boolean
  onClose: () => void
  regionName: string
  entries: CoverageEntry[]
  // Bundles whose legs aren't all countries (CHM: mainland + two SARs) say
  // "regions" instead. See RegionalMeta.coverageUnit.
  unit?: 'countries' | 'regions'
}

export function CoverageModal({ open, onClose, regionName, entries, unit }: Props) {
  const { t } = useTranslation('detail')
  const k = (base: string) => (unit === 'regions' ? `${base}Regions` : base)
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Reset the search box each time the modal is reopened.
  useEffect(() => {
    if (open) setQuery('')
  }, [open])

  const sorted = useMemo(
    () => [...entries].sort((a, b) => a.name.localeCompare(b.name)),
    [entries],
  )
  const mixedSpeeds = useMemo(() => entries.some((e) => /\((?:3|4)G\)/.test(e.nets)), [entries])
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? sorted.filter((e) => e.name.toLowerCase().includes(q)) : sorted
  }, [sorted, query])

  if (!open) return null

  return (
    <div className="cov-modal" role="dialog" aria-modal="true" aria-label={t(`coverageModal.${k('title')}`)}>
      <div className="cov-modal__backdrop" onClick={onClose} />
      <div className="cov-modal__panel">
        <div className="cov-modal__head">
          <div>
            <div className="cov-modal__eyebrow">{regionName}</div>
            <h2 className="cov-modal__title">{t(`coverageModal.${k('title')}`)}</h2>
          </div>
          <button className="cov-modal__close" onClick={onClose} aria-label={t('coverageModal.close')}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="cov-modal__meta">
          <span className="cov-modal__count">{t(`coverageModal.${k('count')}`, { count: entries.length })}</span>
          <span className="cov-modal__speed"><Icon name="signal" size={12} /> {t(mixedSpeeds ? 'coverageModal.speedMixed' : 'coverageModal.speed')}</span>
        </div>

        <div className="cov-modal__search">
          <Icon name="search" size={15} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t(`coverageModal.${k('search')}`)}
            aria-label={t(`coverageModal.${k('search')}`)}
            autoComplete="off"
          />
        </div>

        <div className="cov-modal__list">
          {filtered.length === 0 ? (
            <div className="cov-modal__empty">{t('coverageModal.empty', { query: query.trim() })}</div>
          ) : (
            filtered.map((e) => (
              <div key={e.name} className="cov-row">
                <span className="cov-row__flag">{e.flag}</span>
                <span className="cov-row__name">{e.name}</span>
                <span className="cov-row__nets">
                  {e.nets.split('/').map((n, i) => (
                    <span key={i} className="cov-chip">{n.trim()}</span>
                  ))}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
